/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @file Hlavní skript platformy pro standardní webové technologie.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

// One-line definice závislostí pro snazší merge.
/* eslint-disable max-len */

// Toto je jediný skript, který by měl přistupovat k neobvyklým globálním proměnným.
// Pokud jsou ve zbytku aplikace potřeba jejich data, tak by jim měla být předána v konstruktoru.
/* global kiwi, process */

// Polyfill se načítají jako první modul aplikace
import '../../polyfill';

import * as SentryBrowser from '@sentry/browser';
import * as SentryIntegrations from '@sentry/integrations';
import Bottle from 'bottlejs';
import cssValue from 'css-value';
import fecha from 'fecha';
import genericPool from 'generic-pool-browser';
import DsLocalStorageAdapter from 'js-data-localstorage';
import ICAL from 'ical.js';
import JavascriptStateMachine from 'javascript-state-machine';
import PathBrowserify from 'path-browserify';
import connectToChild from 'penpal/lib/connectToChild';
import UaParser from 'ua-parser-js';
import VeeValidate from 'vee-validate';
import Vue from 'vue';

import AnalyticsTracker from '../analytics/tracker/analytics-tracker';
import AppManager from '../../adapter/web/app/app-manager';
import AssetDownloader from '../asset/downloader/asset-downloader';
import AutorebootCommunicator from '../autoreboot/autoreboot-communicator';
import AutorebootManager from '../autoreboot/autoreboot-manager';
import AutorebootReplacer from '../autoreboot/autoreboot-replacer';
import Communicator from '../communicator/communicator';
import CommunicatorControl from '../communicator/control/communicator-control';
import CommunicatorSession from '../communicator/session/communicator-session';
import CommunicatorSettings from '../communicator/settings/communicator-settings';
import CommunicatorSettingsFactory from '../communicator/settings/communicator-settings-factory';
import ConsoleLogger from '../console/console-logger';
import ConsoleLoggerFactory from '../console/console-logger-factory';
import ContentCommunicator from '../content/communicator/content-communicator';
import ContentTaskCommunicator from '../content/task/communicator/content-task-communicator';
import ContentTaskComparator from '../content/task/comparator/content-task-comparator';
import ContentTaskPlanCron from '../content/task/plan/cron/content-task-plan-cron';
import ContentTaskPlanCronFactory from '../content/task/plan/cron/content-task-plan-cron-factory';
import ContentTaskPlanIterator from '../content/task/plan/iterator/content-task-plan-iterator';
import ContentTaskPlanIteratorFactory from '../content/task/plan/iterator/factory/content-task-plan-iterator-factory';
import ContentTaskPlanUidGenerator from '../content/task/plan/uid-generator/content-task-plan-uid-generator';
import ContentTaskReplacer from '../content/task/replacer/content-task-replacer';
import ContentTaskToTaskConverter from '../content/task/to-task-converter/content-task-to-task-converter';
import Detector from '../detector/detector';
import Device from '../../device/device';
import DhtmlxDateParser from '../dhtmlx/date/dhtmlx-date-parser';
import DhtmlxRecTypeUtil from '../dhtmlx/dhtmlx-rec-type-util';
import DhtmlxToIcalEvent from '../dhtmlx/to-ical/dhtmlx-to-ical-event';
import DhtmlxToIcalEventFactory from '../dhtmlx/to-ical/dhtmlx-to-ical-event-factory';
import DhtmlxToIcalExporter from '../dhtmlx/to-ical/dhtmlx-to-ical-exporter';
import DhtmlxToIcalRecType from '../dhtmlx/to-ical/dhtmlx-to-ical-rec-type';
import DhtmlxToIcalRecTypeFactory from '../dhtmlx/to-ical/dhtmlx-to-ical-rec-type-factory';
import DivViewerSource from '../div/div-viewer-source';
import DivViewerSourceCreator from '../div/div-viewer-source-creator';
import DivViewerSourceFactory from '../div/div-viewer-source-factory';
import Downloader from '../downloader/downloader';
import ErrorLevelFactory from '../error/error-level-factory';
import FileArchiveManager from '../../adapter/web/file/archive/file-archive-manager';
import FileSystemManager from '../../adapter/web/file/system/file-system-manager';
import FileSystemMapper from '../../adapter/web/file/system/file-system-mapper';
import FileSystemPath from '../file/system/file-system-path';
import FileSystemPathFactory from '../file/system/file-system-path-factory';
import FileTransferManager from '../../adapter/web/file/transfer/file-transfer-manager';
import FileTransferUtil from '../file/transfer/file-transfer-util';
import FingerprintBrowser from '../fingerprint/fingerprint-browser';
import FingerprintHasher from '../fingerprint/fingerprint-hasher';
import FirmwareManager from '../../adapter/web/firmware/firmware-manager';
import FocusNavigator from '../focus/focus-navigator';
import FocusNoop from '../focus/focus-noop';
import HeadersUtil from '../headers/headers-util';
import HtmlElementRepairer from '../html/html-element-repairer';
import HtmlIframePreflighterNoop from '../html/html-iframe-preflighter-noop';
import HtmlMediaUtil from '../html/html-media-util';
import HtmlVideoRepairer from '../html/html-video-repairer';
import IcalExpander from '../ical/expander/ical-expander';
import IcalSlicer from '../ical/slicer/ical-slicer';
import IcalTimeUtil from '../ical/ical-time-util';
import IcalUtil from '../ical/ical-util';
import ImageOnlineViewerType from '../image/image-online-viewer-type';
import ImageViewerController from '../image/image-viewer-controller';
import ImageViewerControllerFactory from '../image/image-viewer-controller-factory';
import KioskViewerType from '../kiosk/kiosk-viewer-type';
import LoggerFactory from '../logger/logger-factory';
import LoggerManager from '../logger/logger-manager';
import LongTimers from '../long-timers/long-timers';
import MediumCommunicator from '../medium/communicator/medium-communicator';
import MediumRegistry from '../medium/medium-registry';
import NetworkManager from '../../adapter/web/network/network-manager';
import OrganizationHelper from '../organization/organization-helper';
import PdfOnlineViewerType from '../pdf/pdf-online-viewer-type';
import PdfParent from '../pdf/pdf-parent';
import PdfParentFactory from '../pdf/pdf-parent-factory';
import PdfViewerController from '../pdf/pdf-viewer-controller';
import PdfViewerControllerFactory from '../pdf/pdf-viewer-controller-factory';
import PdfViewerSource from '../pdf/pdf-viewer-source';
import PdfViewerSourceCreator from '../pdf/pdf-viewer-source-creator';
import PdfViewerSourceFactory from '../pdf/pdf-viewer-source-factory';
import Player from '../../player/player';
import PlaylistCommunicator from '../playlist/playlist-communicator';
import Pool from '../pool/pool';
import PoolAsync from '../pool/pool-async';
import PoolAsyncFactory from '../pool/pool-async-factory';
import PoolFactory from '../pool/pool-factory';
import PositionFrame from '../position/position-frame';
import PositionFrameFactory from '../position/position-frame-factory';
import PowerManager from '../../adapter/web/power/power-manager';
import PowerTaskCommunicator from '../power/task/communicator/power-task-communicator';
import PowerTaskReplacer from '../power/task/replacer/power-task-replacer';
import PowerTaskToTaskConverter from '../power/task/to-task-converter/power-task-to-task-converter';
import PromiseUtil from '../promise/promise-util';
import Queue from '../queue/queue';
import QueueFactory from '../queue/queue-factory';
import Replacer from '../replacer/replacer';
import ReplacerCommon from '../replacer/common/replacer-common';
import ReplacerControl from '../replacer/control/replacer-control';
import ReplacerPower from '../replacer/power/replacer-power';
import ReplacerScreen from '../replacer/screen/replacer-screen';
import ReplacerSession from '../replacer/session/replacer-session';
import ReplacerSound from '../replacer/sound/replacer-sound';
import ReportManager from '../report/report-manager';
import RequestUtil from '../request/request-util';
import ScheduleManager from '../schedule/schedule-manager';
import ScreenManager from '../../adapter/web/screen/screen-manager';
import SentryLogger from '../sentry/sentry-logger';
import SentryLoggerFactory from '../sentry/sentry-logger-factory';
import SentryOfflineIntegration from '../sentry/sentry-offline-integration';
import SentrySeverityFactory from '../sentry/sentry-severity-factory';
import SetupError from '../setup/setup-error';
import SetupErrorFactory from '../setup/setup-error-factory';
import SetupManager from '../setup/setup-manager';
import StateMachine from '../state-machine/state-machine';
import StateMachineFactory from '../state-machine/state-machine-factory';
import Store from '../store/store';
import StreamViewerType from '../stream/stream-viewer-type';
import StretchNative from '../stretch/stretch-native';
import StringUtil from '../string/string-util';
import TaskCommunicator from '../task/communicator/task-communicator';
import TaskToContentTaskConverter from '../task/to-content-task-converter/task-to-content-task-converter';
import TaskToPowerTaskConverter from '../task/to-power-task-converter/task-to-power-task-converter';
import TimerCommunicator from '../timer/communicator/timer-communicator';
import TrackCommunicator from '../track/track-communicator';
import TrackPlanCommunicator from '../track/track-plan-communicator';
import Updater from '../updater/updater';
import ViewerControllerVideo from '../viewer/controller/viewer-controller-video';
import ViewerControllerVideoFactory from '../viewer/controller/viewer-controller-video-factory';
import ViewerDelayer from '../viewer/delayer/viewer-delayer';
import ViewerDelayerFactory from '../viewer/delayer/viewer-delayer-factory';
import ViewerDirector from '../viewer/director/viewer-director';
import ViewerDirectorFactory from '../viewer/director/viewer-director-factory';
import ViewerDirectorResource from '../viewer/director/viewer-director-resource';
import ViewerLifeCycle from '../viewer/life-cycle/viewer-life-cycle';
import ViewerLifeCycleFactory from '../viewer/life-cycle/viewer-life-cycle-factory';
import ViewerRegistry from '../../adapter/web/viewer/viewer-registry';
import ViewerSlide from '../viewer/slide/viewer-slide';
import ViewerSlideFactory from '../viewer/slide/viewer-slide-factory';
import ViewerSourceIframe from '../viewer/source/viewer-source-iframe';
import ViewerSourceIframeCreator from '../viewer/source/viewer-source-iframe-creator';
import ViewerSourceIframeFactory from '../viewer/source/viewer-source-iframe-factory';
import ViewerSourceVideo from '../viewer/source/viewer-source-video';
import ViewerSourceVideoCreator from '../viewer/source/viewer-source-video-creator';
import ViewerSourceVideoFactory from '../viewer/source/viewer-source-video-factory';
import ViewerThread from '../viewer/thread/viewer-thread';
import ViewerThreadFactory from '../viewer/thread/viewer-thread-factory';
import ViewerTypeVideo from '../viewer/type/viewer-type-video';
import ViewerTypeWeb from '../viewer/type/viewer-type-web';
import VirtualKeyboardHidden from '../virtual-keyboard/virtual-keyboard-hidden';
import VirtualKeyboardHiddenFactory from '../virtual-keyboard/virtual-keyboard-hidden-factory';
import VirtualKeyboardManager from '../../adapter/web/virtual-keyboard/virtual-keyboard-manager';
import VueFactory from '../vue/vue-factory';
import WebControlListener from '../web/control/web-control-listener';
import WebControlManager from '../web/control/web-control-manager';
import WebControlParser from '../web/control/web-control-parser';
import WebDriveManager from '../web/drive/web-drive-manager';
import WebMemoryManager from '../web/memory/web-memory-manager';
import WebNetworkListener from '../web/web-network-listener';
import WebSensorManager from '../web/sensor/web-sensor-manager';
import WebSoundManager from '../web/sound/web-sound-manager';
import WebStartupManager from '../web/startup/web-startup-manager';
import WebTimeManager from '../web/time/web-time-manager';
import config from '../../config';
import htmlImageBlank from '../html/html-image-blank.png';
import htmlVideoBlank from '../html/html-video-blank.mp4';

// Inicializace IoC/DI knihovny.
Bottle.config.strict = true;
const bottle = new Bottle();
// Inicializace loggeru, aby šlo zaznamenat chyby co nejdříve.
// Deaktivovaný Prettier pro one-line definice závislostí a snazší merge.
// prettier-ignore
new Promise((resolve) => resolve(
  bottle
    .constant('Console', console)
    .constant('ConsoleLoggerConstructor', ConsoleLogger)
    .constant('SentryBrowser', SentryBrowser)
    .constant('SentryLoggerConstructor', SentryLogger)
    .constant('SentryOfflineIntegrationOptions', { key: `${config.name}/sentryEvent`, maxEvents: config.sentryMaxEvents })
    .constant('SentryVueIntegrationOptions', { Vue })
    .constant('buildId', kiwi.buildId)
    .constant('env', process.env.NODE_ENV)
    .constant('sentryCaptureErrorLevel', config.sentryCaptureErrorLevel)
    .constant('sentryDsn', kiwi.sentryDsn)
    .service('ConsoleLoggerFactory', ConsoleLoggerFactory, 'ConsoleLoggerConstructor', 'Console', 'ErrorLevelFactory')
    .service('ErrorLevelFactory', ErrorLevelFactory)
    .service('SentryLoggerFactory', SentryLoggerFactory, 'SentryBrowser', 'SentryLoggerConstructor', 'sentryCaptureErrorLevel', 'ErrorLevelFactory', 'SentryOfflineIntegration', 'SentrySeverityFactory', 'SentryVueIntegration')
    .service('SentryOfflineIntegration', SentryOfflineIntegration, 'SentryOfflineIntegrationOptions')
    .service('SentrySeverityFactory', SentrySeverityFactory)
    .service('SentryVueIntegration', SentryIntegrations.Vue, 'SentryVueIntegrationOptions')
    .serviceFactory('Logger', LoggerFactory, 'buildId', 'ConsoleLoggerFactory', 'env', 'sentryDsn', 'SentryLoggerFactory')
    .container.Logger
))
  // Inicializace a spuštění Player aplikace.
  .then(() => (
    bottle
      .constant('CommunicatorSettingsConstructor', CommunicatorSettings)
      .constant('Config', config)
      .constant('ContentTaskPlanCronConstructor', ContentTaskPlanCron)
      .constant('ContentTaskPlanIteratorConstructor', ContentTaskPlanIterator)
      .constant('CssValue', cssValue)
      .constant('DhtmlxToIcalEventConstructor', DhtmlxToIcalEvent)
      .constant('DhtmlxToIcalRecTypeConstructor', DhtmlxToIcalRecType)
      .constant('DivViewerSourceConstructor', DivViewerSource)
      .constant('Document', document)
      .constant('DsLocalStorageAdapterOptions', { basePath: config.name })
      .constant('Fecha', fecha)
      .constant('FileSystemPathConstructor', FileSystemPath)
      .constant('GenericPool', genericPool)
      .constant('IcalApi', ICAL)
      .constant('ImageViewerControllerConstructor', ImageViewerController)
      .constant('JavascriptStateMachine', JavascriptStateMachine)
      .constant('MediumRegistryConstructor', MediumRegistry)
      .constant('Navigator', navigator)
      .constant('PathBrowserify', PathBrowserify)
      .constant('PdfParentConstructor', PdfParent)
      .constant('PdfViewerControllerConstructor', PdfViewerController)
      .constant('PdfViewerSourceConstructor', PdfViewerSource)
      .constant('PenpalConnectToChild', connectToChild)
      .constant('PoolAsyncConstructor', PoolAsync)
      .constant('PoolConstructor', Pool)
      .constant('PositionFrameConstructor', PositionFrame)
      .constant('QueueConstructor', Queue)
      .constant('SetupErrorConstructor', SetupError)
      .constant('StateMachineConstructor', StateMachine)
      .constant('VeeValidate', VeeValidate)
      .constant('ViewerControllerVideoConstructor', ViewerControllerVideo)
      .constant('ViewerDelayerConstructor', ViewerDelayer)
      .constant('ViewerDelayerOptions', { delay: config.viewer.errorDelay, limit: config.viewer.errorLimit })
      .constant('ViewerDirectorConstructor', ViewerDirector)
      .constant('ViewerLifeCycleConstructor', ViewerLifeCycle)
      .constant('ViewerSlideConstructor', ViewerSlide)
      .constant('ViewerSourceIframeConstructor', ViewerSourceIframe)
      .constant('ViewerSourceVideoConstructor', ViewerSourceVideo)
      .constant('ViewerThreadConstructor', ViewerThread)
      .constant('ViewerThreadOptions', { loadLimit: config.viewer.loadLimit })
      .constant('VirtualKeyboardHiddenConstructor', VirtualKeyboardHidden)
      .constant('VueConstructor', Vue)
      .constant('Window', window)
      .constant('autorebootDefaultDisabled', config.autorebootDefaultDisabled)
      .constant('autorebootDefaultInterval', config.autorebootDefaultInterval)
      .constant('autorebootMinInterval', config.autorebootMinInterval)
      .constant('contentTaskMaxInterval', config.contentTaskMaxInterval)
      .constant('defaultOrientationEnum', config.device.orientation.defaultOrientationEnum)
      .constant('htmlIframeLoadDelay', config.htmlIframeLoadDelay)
      .constant('htmlVideoBlank', htmlVideoBlank)
      .constant('longTimersMaxDelay', config.longTimersMaxDelay)
      .constant('origin', location.origin)
      .constant('pdfConnectionTimeout', config.pdfConnectionTimeout)
      .constant('pdfSourceMax', config.pdfSourceMax)
      .constant('viewerControllerVideoPoster', htmlImageBlank)
      .service('AnalyticsTracker', AnalyticsTracker, 'QueueFactory', 'Session', 'Store')
      .service('AppManager', AppManager)
      .service('AssetDownloader', AssetDownloader, 'AssetDownloaderBasePath', 'FileArchiveManager', 'FileSystemPathFactory', 'FileSystemManager', 'FileTransferManager', 'Logger', 'PathBrowserify', 'PromiseUtil', 'Store')
      .service('AutorebootCommunicator', AutorebootCommunicator)
      .service('AutorebootManager', AutorebootManager, 'Logger', 'autorebootMinInterval', 'PowerManager', 'LongTimers')
      .service('AutorebootReplacer', AutorebootReplacer, 'autorebootDefaultDisabled', 'autorebootDefaultInterval', 'AutorebootManager')
      .service('Communicator', Communicator, 'ContentCommunicator')
      .service('CommunicatorControl', CommunicatorControl)
      .service('CommunicatorSession', CommunicatorSession, 'AutorebootCommunicator', 'CommunicatorControl', 'Session')
      .service('CommunicatorSettingsFactory', CommunicatorSettingsFactory, 'CommunicatorSettingsConstructor')
      .service('ContentCommunicator', ContentCommunicator, 'CommunicatorSession', 'CommunicatorSettingsFactory', 'Config', 'ContentTaskCommunicator', 'FirmwareManager', 'MediumCommunicator', 'PlaylistCommunicator', 'PowerTaskCommunicator', 'TaskCommunicator')
      .service('ContentTaskCommunicator', ContentTaskCommunicator, 'DhtmlxDateParser', 'DhtmlxRecTypeUtil', 'DhtmlxToIcalExporter', 'ContentTaskPlanUidGenerator')
      .service('ContentTaskComparator', ContentTaskComparator)
      .service('ContentTaskPlanCronFactory', ContentTaskPlanCronFactory, 'ContentTaskPlanCronConstructor', 'ContentTaskComparator', 'ContentTaskPlanIteratorFactory', 'LongTimers')
      .service('ContentTaskPlanIteratorFactory', ContentTaskPlanIteratorFactory, 'ContentTaskPlanIteratorConstructor', 'IcalApi', 'IcalExpander', 'contentTaskMaxInterval', 'IcalTimeUtil')
      .service('ContentTaskPlanUidGenerator', ContentTaskPlanUidGenerator)
      .service('ContentTaskReplacer', ContentTaskReplacer, 'Store', 'TaskToContentTaskConverter')
      .service('ContentTaskToTaskConverter', ContentTaskToTaskConverter)
      .service('ControlListener', WebControlListener, 'ControlParser')
      .service('ControlManager', WebControlManager, 'ControlListener')
      .service('ControlParser', WebControlParser)
      .service('Detector', Detector, 'DriveManager')
      .service('Device', Device, 'buildId', 'AnalyticsTracker', 'Communicator', 'ContentTaskPlanCronFactory', 'FingerprintBrowser', 'FirmwareManager', 'Logger', 'LoggerManager', 'NetworkListener', 'PowerManager', 'Replacer', 'ReportManager', 'ScheduleManager', 'Session', 'StartupManager', 'Store', 'Updater', 'ViewerDirectorResource', 'ViewerRegistry')
      .service('DhtmlxDateParser', DhtmlxDateParser, 'Fecha')
      .service('DhtmlxRecTypeUtil', DhtmlxRecTypeUtil, 'StringUtil')
      .service('DhtmlxToIcalEventFactory', DhtmlxToIcalEventFactory, 'DhtmlxToIcalEventConstructor', 'IcalApi', 'DhtmlxDateParser')
      .service('DhtmlxToIcalExporter', DhtmlxToIcalExporter, 'IcalApi', 'DhtmlxToIcalEventFactory', 'DhtmlxToIcalRecTypeFactory', 'IcalTimeUtil', 'IcalUtil')
      .service('DhtmlxToIcalRecTypeFactory', DhtmlxToIcalRecTypeFactory, 'DhtmlxToIcalRecTypeConstructor', 'IcalApi', 'IcalUtil', 'DhtmlxRecTypeUtil')
      .service('DivViewerSourceCreator', DivViewerSourceCreator, 'PoolFactory', 'DivViewerSourceFactory')
      .service('DivViewerSourceFactory', DivViewerSourceFactory, 'DivViewerSourceConstructor', 'PositionFrameFactory')
      .service('Downloader', Downloader, 'AssetDownloader', 'Config', 'Detector', 'Logger', 'Store', 'Session', 'ViewerRegistry')
      .service('DriveManager', WebDriveManager)
      .service('DsLocalStorageAdapter', DsLocalStorageAdapter, 'DsLocalStorageAdapterOptions')
      .service('FileArchiveManager', FileArchiveManager)
      .service('FileSystemManager', FileSystemManager)
      .service('FileSystemMapper', FileSystemMapper)
      .service('FileSystemPathFactory', FileSystemPathFactory, 'FileSystemPathConstructor', 'PathBrowserify')
      .service('FileTransferManager', FileTransferManager)
      .service('FileTransferUtil', FileTransferUtil, 'HeadersUtil', 'Logger', 'RequestUtil')
      .service('FingerprintBrowser', FingerprintBrowser, 'FingerprintHasher')
      .service('FingerprintHasher', FingerprintHasher)
      .service('FirmwareManager', FirmwareManager, 'UaParser')
      .service('FocusManager', FocusNoop, 'ControlManager', 'FocusNavigator')
      .service('FocusNavigator', FocusNavigator, 'Document')
      .service('HeadersUtil', HeadersUtil)
      .service('HtmlElementRepairer', HtmlElementRepairer)
      .service('HtmlIframePreflighterNoop', HtmlIframePreflighterNoop)
      .service('HtmlMediaUtil', HtmlMediaUtil)
      .service('HtmlVideoRepairer', HtmlVideoRepairer, 'htmlVideoBlank', 'HtmlElementRepairer', 'HtmlMediaUtil')
      .service('IcalExpander', IcalExpander, 'IcalSlicer', 'IcalUtil')
      .service('IcalSlicer', IcalSlicer, 'IcalApi', 'IcalTimeUtil', 'IcalUtil')
      .service('IcalTimeUtil', IcalTimeUtil, 'IcalApi')
      .service('IcalUtil', IcalUtil, 'IcalApi')
      .service('ImageViewerControllerFactory', ImageViewerControllerFactory, 'ImageViewerControllerConstructor', 'ViewerLifeCycleFactory')
      .service('ImageViewerType', ImageOnlineViewerType, 'ImageViewerControllerFactory', 'DivViewerSourceCreator')
      .service('KioskViewerType', KioskViewerType, 'htmlIframeLoadDelay', 'HtmlIframePreflighterNoop', 'ViewerSourceIframeCreator')
      .service('LoggerManager', LoggerManager, 'AppManager', 'FirmwareManager', 'Logger')
      .service('LongTimers', LongTimers, 'longTimersMaxDelay')
      .service('MediumCommunicator', MediumCommunicator)
      .service('MemoryManager', WebMemoryManager)
      .service('NetworkListener', WebNetworkListener, 'Window', 'Navigator')
      .service('NetworkManager', NetworkManager)
      .service('OrganizationHelper', OrganizationHelper)
      .service('PdfOnlineViewerType', PdfOnlineViewerType, 'PdfViewerControllerFactory', 'PdfViewerSourceCreator')
      .service('PdfParentFactory', PdfParentFactory, 'PdfParentConstructor', 'PenpalConnectToChild', 'origin', 'pdfConnectionTimeout')
      .service('PdfViewerControllerFactory', PdfViewerControllerFactory, 'PdfViewerControllerConstructor', 'ViewerLifeCycleFactory')
      .service('PdfViewerSourceCreator', PdfViewerSourceCreator, 'pdfSourceMax', 'PoolAsyncFactory', 'PdfViewerSourceFactory')
      .service('PdfViewerSourceFactory', PdfViewerSourceFactory, 'PdfViewerSourceConstructor', 'ViewerSourceIframeCreator', 'PdfParentFactory')
      .service('Player', Player, 'AppManager', 'ControlManager', 'Device', 'FocusManager', 'Logger', 'NetworkListener', 'ScreenManager', 'Session', 'SetupManager', 'VirtualKeyboardManager', 'VueFactory')
      .service('PlaylistCommunicator', PlaylistCommunicator, 'TrackCommunicator')
      .service('PoolAsyncFactory', PoolAsyncFactory, 'PoolAsyncConstructor', 'GenericPool')
      .service('PoolFactory', PoolFactory, 'PoolConstructor')
      .service('PositionFrameFactory', PositionFrameFactory, 'PositionFrameConstructor', 'CssValue', 'StateMachineFactory')
      .service('PowerManager', PowerManager)
      .service('PowerTaskCommunicator', PowerTaskCommunicator, 'TimerCommunicator')
      .service('PowerTaskReplacer', PowerTaskReplacer, 'Store', 'TaskToPowerTaskConverter')
      .service('PowerTaskToTaskConverter', PowerTaskToTaskConverter)
      .service('PromiseUtil', PromiseUtil)
      .service('QueueFactory', QueueFactory, 'QueueConstructor')
      .service('Replacer', Replacer, 'AutorebootReplacer', 'ContentTaskReplacer', 'Downloader', 'Logger', 'PowerTaskReplacer', 'Store', 'PromiseUtil', 'QueueFactory', 'ReplacerCommon', 'ReplacerControl', 'ReplacerPower', 'ReplacerScreen', 'ReplacerSession', 'ReplacerSound')
      .service('ReplacerCommon', ReplacerCommon, 'Store')
      .service('ReplacerControl', ReplacerControl, 'ControlManager', 'Logger')
      .service('ReplacerPower', ReplacerPower, 'PowerManager')
      .service('ReplacerScreen', ReplacerScreen, 'defaultOrientationEnum', 'ScreenManager')
      .service('ReplacerSession', ReplacerSession, 'Store', 'Session')
      .service('ReplacerSound', ReplacerSound, 'SoundManager')
      .service('ReportManager', ReportManager, 'AppManager', 'ControlManager', 'DriveManager', 'FirmwareManager', 'Logger', 'MemoryManager', 'NetworkManager', 'PowerManager', 'ScreenManager', 'SensorManager', 'SoundManager', 'TimeManager')
      .service('RequestUtil', RequestUtil, 'HeadersUtil')
      .service('ScheduleManager', ScheduleManager, 'ViewerRegistry')
      .service('ScreenManager', ScreenManager, 'defaultOrientationEnum')
      .service('SensorManager', WebSensorManager)
      .service('SetupErrorFactory', SetupErrorFactory, 'SetupErrorConstructor')
      .service('SetupManager', SetupManager, 'OrganizationHelper', 'SetupErrorFactory')
      .service('SoundManager', WebSoundManager)
      .service('StartupManager', WebStartupManager)
      .service('StateMachineFactory', StateMachineFactory, 'JavascriptStateMachine', 'StateMachineConstructor')
      .service('StreamViewerType', StreamViewerType, 'ViewerControllerVideoFactory', 'VideoViewerSourceCreator')
      .service('StretchNative', StretchNative)
      .service('StringUtil', StringUtil)
      .service('TaskCommunicator', TaskCommunicator, 'ContentTaskToTaskConverter', 'PowerTaskToTaskConverter')
      .service('TaskToContentTaskConverter', TaskToContentTaskConverter, 'IcalApi', 'IcalTimeUtil', 'IcalUtil', 'ContentTaskPlanUidGenerator')
      .service('TaskToPowerTaskConverter', TaskToPowerTaskConverter)
      .service('TimeManager', WebTimeManager)
      .service('TimerCommunicator', TimerCommunicator)
      .service('TrackCommunicator', TrackCommunicator, 'TrackPlanCommunicator')
      .service('TrackPlanCommunicator', TrackPlanCommunicator, 'DhtmlxToIcalExporter')
      .service('UaParser', UaParser)
      .service('Updater', Updater, 'AppManager', 'buildId', 'Communicator', 'Session', 'Store')
      .service('VideoViewerSourceCreator', ViewerSourceVideoCreator, 'PoolFactory', 'HtmlVideoRepairer', 'VideoViewerSourceFactory')
      .service('VideoViewerSourceFactory', ViewerSourceVideoFactory, 'ViewerSourceVideoConstructor', 'PositionFrameFactory')
      .service('VideoViewerType', ViewerTypeVideo, 'VideoViewerSourceCreator', 'ViewerControllerVideoFactory')
      .service('ViewerControllerVideoFactory', ViewerControllerVideoFactory, 'ViewerControllerVideoConstructor', 'HtmlMediaUtil', 'StretchNative', 'ViewerLifeCycleFactory', 'viewerControllerVideoPoster')
      .service('ViewerDelayerFactory', ViewerDelayerFactory, 'ViewerDelayerConstructor', 'ViewerDelayerOptions')
      .service('ViewerDirectorFactory', ViewerDirectorFactory, 'ViewerDirectorConstructor', 'QueueFactory', 'ViewerThreadFactory')
      .service('ViewerDirectorResource', ViewerDirectorResource, 'ViewerDirectorFactory')
      .service('ViewerLifeCycleFactory', ViewerLifeCycleFactory, 'ViewerLifeCycleConstructor', 'StateMachineFactory')
      .service('ViewerRegistry', ViewerRegistry, 'MediumRegistryConstructor', 'ImageViewerType', 'KioskViewerType', 'PdfOnlineViewerType', 'StreamViewerType', 'VideoViewerType', 'ViewerTypeWeb')
      .service('ViewerSlideFactory', ViewerSlideFactory, 'ViewerSlideConstructor', 'StateMachineFactory')
      .service('ViewerSourceIframeCreator', ViewerSourceIframeCreator, 'PoolFactory', 'ViewerSourceIframeFactory')
      .service('ViewerSourceIframeFactory', ViewerSourceIframeFactory, 'ViewerSourceIframeConstructor', 'PositionFrameFactory')
      .service('ViewerThreadFactory', ViewerThreadFactory, 'ViewerThreadConstructor', 'ViewerDelayerFactory', 'Logger', 'NetworkListener', 'ViewerSlideFactory', 'ViewerThreadOptions')
      .service('ViewerTypeWeb', ViewerTypeWeb, 'htmlIframeLoadDelay', 'HtmlIframePreflighterNoop', 'ViewerSourceIframeCreator')
      .service('VirtualKeyboardHiddenFactory', VirtualKeyboardHiddenFactory, 'VirtualKeyboardHiddenConstructor')
      .service('VirtualKeyboardManager', VirtualKeyboardManager, 'VirtualKeyboardHiddenFactory')
      .service('VueFactory', VueFactory, 'VeeValidate', 'VueConstructor')
      .serviceFactory(
        'AssetDownloaderBasePath',
        (pathFactory) => pathFactory.fromValue('/internal'),
        'FileSystemPathFactory'
      )
      .serviceFactory(
        'Session',
        (store) => store.inject('session', { id: config.device.session.defaultId }),
        'Store'
      )
      .serviceFactory(
        'Store',
        (storage) => new Store({ storage }),
        'DsLocalStorageAdapter'
      )
      .container.Player.start('#player')
  ))
  .catch((error) => bottle.container.Logger.error(error));
